import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { FiBookOpen } from "react-icons/fi";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { errorAlert } from "../../Components/Alerts";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import "./JournalHistoryPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

function formatDateForDisplay(date) {
    if (!date) {
        return "";
    }

    const raw = String(date).trim();
    if (!raw) {
        return "";
    }

    const dotMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dotMatch) {
        const [, year, month, day] = dotMatch;
        return `${day}.${month}.${year}`;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
        return raw;
    }

    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    return `${day}.${month}.${year}`;
}

export default function JournalHistoryPage() {
    const headers = authHeader();
    const { fiscalYear } = useContext(FiscalYearContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [quickFilter, setQuickFilter] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [totals, setTotals] = useState({ debit: 0, credit: 0, balance: 0 });

    const columns = useMemo(() => ([
        { field: 'rowNumber', headerName: '#', minWidth: 70, width: 80 },
        { field: 'actionType', headerName: 'Tipi Veprimit', minWidth: 160, width: 190 },
        { field: 'ledger', headerName: 'Llogaria Kontabel', minWidth: 220, width: 260 },
        { field: 'date', headerName: 'Data', minWidth: 120, width: 140 },
        {
            field: 'debit',
            headerName: 'Debi',
            minWidth: 130,
            width: 150,
            valueFormatter: (value) => formatCurrency(value),
        },
        {
            field: 'credit',
            headerName: 'Kredi',
            minWidth: 130,
            width: 150,
            valueFormatter: (value) => formatCurrency(value),
        },
        {
            field: 'balance',
            headerName: 'Bilanci',
            minWidth: 130,
            width: 150,
            valueFormatter: (value) => formatCurrency(value),
        },
        { field: 'comment', headerName: 'Komenti', minWidth: 280, width: 360 },
    ]), []);

    const rows = useMemo(() => (
        data.map((item, index) => ({
            id: item.JournalID || `journal-${index}`,
            rowNumber: item.JournalID || (paginationModel.page * paginationModel.pageSize + index + 1),
            actionType: item.ActionType || '',
            ledger: item.LedgerNameEN || '',
            date: formatDateForDisplay(item.Date),
            debit: item.Debit || 0,
            credit: item.Credit || 0,
            balance: item.Balance || 0,
            comment: item.Comment || '',
            rowInfo: item,
        }))
    ), [data, paginationModel.page, paginationModel.pageSize]);

    const fetchTotals = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + 'JournalHistory/getAllJournalHistoryData', {
                headers,
                params: {
                    fiscalYear,
                    page: 0,
                    pageSize: 1000000,
                    sort: JSON.stringify([]),
                    filter: JSON.stringify(filterModel.items || []),
                    quickFilter: quickFilter.join(' '),
                },
            });

            const payload = response?.data;
            const items = Array.isArray(payload)
                ? payload
                : (payload?.items || payload?.data || []);

            const debit = items.reduce((sum, item) => sum + Number(item?.Debit || 0), 0);
            const credit = items.reduce((sum, item) => sum + Number(item?.Credit || 0), 0);

            setTotals({
                debit,
                credit,
                balance: debit - credit,
            });
        } catch {
            setTotals({ debit: 0, credit: 0, balance: 0 });
        }
    };

    const fetchExportRows = async () => {
        const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);

        const response = await axios.get(ApiLink.API_URL + 'JournalHistory/getAllJournalHistoryData', {
            headers,
            params: {
                fiscalYear,
                page: 0,
                pageSize: totalRowsToExport,
                sort: JSON.stringify(sortModel),
                filter: JSON.stringify(filterModel.items || []),
                quickFilter: quickFilter.join(' '),
            },
        });

        const payload = response?.data;
        const items = Array.isArray(payload)
            ? payload
            : (payload?.items || payload?.data || []);

        return items.map((item, index) => ({
            id: item.JournalID || `journal-export-${index}`,
            rowNumber: item.JournalID || (index + 1),
            actionType: item.ActionType || '',
            ledger: item.LedgerNameEN || '',
            date: formatDateForDisplay(item.Date),
            debit: item.Debit || 0,
            credit: item.Credit || 0,
            balance: item.Balance || 0,
            comment: item.Comment || '',
            rowInfo: item,
        }));
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(ApiLink.API_URL + 'JournalHistory/getAllJournalHistoryData', {
                headers,
                params: {
                    fiscalYear,
                    page: paginationModel.page,
                    pageSize: paginationModel.pageSize,
                    sort: JSON.stringify(sortModel),
                    filter: JSON.stringify(filterModel.items || []),
                    quickFilter: quickFilter.join(' '),
                },
            });

            const payload = response?.data;
            const items = Array.isArray(payload)
                ? payload
                : (payload?.items || payload?.data || []);
            const total = Array.isArray(payload)
                ? items.length
                : Number(payload?.total ?? payload?.totalRows ?? items.length);

            setData(items);
            setRowCount(total);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fiscalYear, paginationModel, sortModel, filterModel, quickFilter]);

    useEffect(() => {
        fetchTotals();
    }, [fiscalYear, filterModel, quickFilter]);

    const summaryFooter = useMemo(() => ({
        labelField: 'ledger',
        label: 'Totali',
        values: {
            debit: formatCurrency(totals.debit),
            credit: formatCurrency(totals.credit),
            balance: formatCurrency(totals.balance),
        },
    }), [totals]);

    return (
        <div className="Crud__Container JHRP__page">
            <div className="JHRP__content">
                <CrudPageHeader
                    icon={FiBookOpen}
                    title="Ditari i Transaksioneve"
                    description="Ky format i Ditarit është i përshtatur për të verifikuar transaksionet."
                    className="JHRP__header"
                />

                <div className="JHRP__reportGrid">
                    <ReusableAccountBooksGrid
                        columns={columns}
                        rows={rows}
                        getRowClassName={(row) => Number(row.balance || 0) === 0 ? 'JHRP__row--zeroBalance' : ''}
                        loading={loading}
                        rowCount={rowCount}
                        height={650}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                        sortModel={sortModel}
                        setSortModel={setSortModel}
                        filterModel={filterModel}
                        setFilterModel={setFilterModel}
                        quickFilter={quickFilter}
                        setQuickFilter={setQuickFilter}
                        showViewAction={false}
                        showEditAction={false}
                        showDeleteAction={false}
                        showExportButton={true}
                        getExportRows={fetchExportRows}
                        summaryFooter={summaryFooter}
                    />
                </div>
            </div>
        </div>
    );
}